import { Link } from '@tanstack/react-router';
import { useAtomValue } from 'jotai';
import { DateTime } from 'luxon';
import { HTMLAttributes, useRef } from 'react';
import styled from 'styled-components';

import { TextButtonLink } from '@creditornot/cb-button';
import { legacyToolsTypographyCss, typographyClassName } from '@creditornot/cb-ingredients';
import { color } from '@creditornot/cb-ingredients/design-tokens';
import { DevIndicator } from 'common/components/cookbook/DevIndicator';
import { Stack } from 'common/components/cookbook/Stack';
import { ThinScrollbarContainer } from 'common/components/cookbook/ThinScrollbarContainer';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { useUserLocale } from 'common/hooks/useUserLocale';
import { useWarpMerchant } from 'common/hooks/useWarpMerchant';
import { showInstallAppButtonAtom } from 'common/state/installAppState';
import { useTaskNotificationToast } from 'common/task-management/useTaskNotificationToast';

import { AppInstallButton } from './AppInstallButton';
import { NavigationLinks } from './NavigationLinks';
import { NavigationUserInfo } from './NavigationUserInfo';

const ContentContainer = styled(Stack)`
  padding: 16px;
  min-height: 100%;
`;

const LogoSection = styled.div`
  padding: 4px 8px 0;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const localHostnames = ['localhost', '127.0.0.1'];

const isLocalhost = localHostnames.includes(window.location.hostname);

const MerchantName = styled.div`
  ${legacyToolsTypographyCss.Heading6()}
  margin: 0;
  color: ${color.text};
  max-width: ${!isLocalhost && import.meta.env.VITE_APP_ENV === 'development' ? 175 : 232}px;
`;

const StyledDevIndicator = styled(DevIndicator)`
  position: relative;
  top: -2px;
`;

const ApplicationVersion = styled(Stack).attrs({ className: typographyClassName.footnote })`
  color: ${color.textSubdued};
  margin-top: 24px;
  padding-left: 8px;
  padding-right: 8px;
`;

const LogoWithInstallWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  position: relative;
`;

type Props = HTMLAttributes<HTMLDivElement> & {
  onNavigate?: () => void;
};

export function NavigationMenu({ onNavigate, ...rest }: Props) {
  const f = useFormatMessage();
  const showInstallBtn = useAtomValue(showInstallAppButtonAtom);
  const logoContainerRef = useRef<HTMLDivElement | null>(null);
  const locale = useUserLocale();
  const releaseDate = DateTime.fromMillis(Number(__RELEASE_TIMESTAMP__)).toLocaleString(
    DateTime.DATETIME_SHORT,
    { locale },
  );

  const { merchant } = useWarpMerchant();

  useTaskNotificationToast();

  return (
    <ThinScrollbarContainer {...rest}>
      <ContentContainer justifyContent="space-between">
        <div>
          <LogoWithInstallWrapper>
            <LogoSection ref={logoContainerRef}>
              <Link to="/" onClick={onNavigate}>
                <MerchantName>{merchant?.name ?? ''}</MerchantName>
              </Link>
              <StyledDevIndicator />
            </LogoSection>
            {showInstallBtn && <AppInstallButton />}
          </LogoWithInstallWrapper>
          <NavigationUserInfo onNavigate={onNavigate} />
          <NavigationLinks onClick={onNavigate} />
        </div>
        <ApplicationVersion>
          <span>
            {f('app.name')} v.{__SHORT_VERSION_HASH__}{' '}
            {__PR_NUMBER__ && (
              <TextButtonLink
                size="inherit"
                href={`https://github.com/creditornot/wm-fulfillment-tools/pull/${__PR_NUMBER__}`}
              >
                #{__PR_NUMBER__}
              </TextButtonLink>
            )}
          </span>
          {f('app.release', { datetime: releaseDate })}
        </ApplicationVersion>
      </ContentContainer>
    </ThinScrollbarContainer>
  );
}
